<template>
  <div class="page-wrap">

    <!-- No data state -->
    <template v-if="!store.hasData">
      <div class="state-no-data">
        <div class="nd-icon">📋</div>
        <h2>ยังไม่มีข้อมูล</h2>
        <p>กรุณาอัปโหลดไฟล์ Excel ก่อนเพื่อให้ระบบประมวลผลข้อมูลลูกค้า</p>
        <a class="btn-upload" href="#" @click.prevent="router.push({ name: 'CsUpload' })">ไปหน้าอัปโหลด</a>
      </div>
    </template>

    <template v-else>
      <!-- Page header -->
      <div class="page-header">
        <div class="page-title-wrap">
          <span class="page-icon">🔮</span>
          <div>
            <div class="page-title">พยากรณ์ตั๋วใกล้หลุด</div>
            <div class="page-subtitle">
              ตั๋ว OPEN ที่กำลังครบกำหนดใน {{ days }} วัน พร้อม % ความน่าจะหลุดจากพฤติกรรมในอดีต
            </div>
          </div>
        </div>

        <!-- Controls -->
        <div class="header-controls">
          <div class="days-filter">
            <span class="filter-label">ดูล่วงหน้า</span>
            <button
v-for="d in dayOptions" :key="d"
              :class="['day-btn', days === d ? 'active' : '']"
              @click="setDays(d)">{{ d }} วัน</button>
          </div>
          <div class="days-filter">
            <span class="filter-label">แสดงสูงสุด</span>
            <button
v-for="l in limitOptions" :key="l"
              :class="['day-btn', limit === l ? 'active' : '']"
              @click="limit = l">{{ l }} รายการ</button>
          </div>
          <button
:class="['toggle-btn', hideOverdue ? 'active' : '']"
            @click="hideOverdue = !hideOverdue">
            {{ hideOverdue ? '✓ ซ่อนตั๋วเกินกำหนด' : 'ซ่อนตั๋วเกินกำหนด' }}
          </button>
          <div v-if="assetTypeOptions.length > 0" class="days-filter">
            <span class="filter-label">ประเภท</span>
            <select v-model="filterType" class="type-select">
              <option value="">ทั้งหมด</option>
              <option v-for="t in assetTypeOptions" :key="t" :value="t">{{ t }}</option>
            </select>
          </div>
          <div class="days-filter">
            <span class="filter-label">ผลัด</span>
            <select v-model="filterPhlat" class="type-select">
              <option value="">ทั้งหมด</option>
              <option value="yes">ตั๋วผลัด</option>
              <option value="no">ไม่ผลัด</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Summary cards -->
      <div class="summary-row">
        <div class="stat-card">
          <div class="stat-value">{{ visibleItems.length }}</div>
          <div class="stat-label">ตั๋วที่ใกล้ครบ</div>
        </div>
        <div class="stat-card stat-high">
          <div class="stat-value">{{ highCount }}</div>
          <div class="stat-label">เสี่ยงสูง (≥65%)</div>
        </div>
        <div class="stat-card stat-mid">
          <div class="stat-value">{{ midCount }}</div>
          <div class="stat-label">เสี่ยงปานกลาง</div>
        </div>
        <div class="stat-card stat-principal">
          <div class="stat-value">{{ fmtBaht(totalPrincipal) }}</div>
          <div class="stat-label">เงินต้นที่เสี่ยงหลุด</div>
        </div>
      </div>

      <!-- Loading / error / empty -->
      <div v-if="loading" class="state-inline">
        <span class="spinner"></span> กำลังโหลดข้อมูล…
      </div>
      <div v-else-if="error" class="state-inline state-error">⚠️ {{ error }}</div>
      <div v-else-if="visibleItems.length === 0" class="state-inline">
        {{ items.length === 0 ? `ไม่มีตั๋วครบกำหนดใน ${days} วันข้างหน้า 🎉` : 'ไม่มีตั๋วที่ยังไม่เกินกำหนดในช่วงเวลานี้' }}
      </div>

      <!-- Table -->
      <div v-else class="section-card">
        <div class="section-head">
          <span class="section-icon">🎫</span>
          <div style="flex:1">
            <div class="section-title">รายการตั๋วที่คาดว่าเสี่ยงหลุด</div>
            <div class="section-subtitle">เรียงจาก % ความน่าจะหลุดสูงที่สุด → ต่ำที่สุด</div>
          </div>
          <span class="badge-count">{{ visibleItems.length }} ตั๋ว</span>
          <button class="export-btn" @click="printPage">Export to PDF</button>
        </div>

        <!-- Print-only header (hidden on screen) -->
        <div class="print-header">
          <div class="ph-title">รายการตั๋วที่คาดว่าเสี่ยงหลุด</div>
          <div class="ph-meta">
            ดูล่วงหน้า {{ days }} วัน · {{ visibleItems.length }} ตั๋ว
            <span v-if="filterType"> · ประเภท: {{ filterType }}</span>
            <span v-if="filterPhlat === 'yes'"> · ตั๋วผลัดเท่านั้น</span>
            <span v-if="filterPhlat === 'no'"> · ไม่ผลัดเท่านั้น</span>
            <span v-if="hideOverdue"> · ซ่อนตั๋วเกินกำหนด</span>
          </div>
          <div class="ph-principal">
            เงินต้นรวม ฿{{ fmtBaht(totalPrincipal) }}
            &nbsp;·&nbsp; เสี่ยงสูง ฿{{ fmtBaht(highPrincipal) }}
            &nbsp;·&nbsp; ปานกลาง ฿{{ fmtBaht(midPrincipal) }}
            &nbsp;·&nbsp; ต่ำ ฿{{ fmtBaht(lowPrincipal) }}
          </div>
        </div>

        <!-- Principal summary strip -->
        <div class="principal-strip">
          <div class="ps-total">
            <span class="ps-lbl">เงินต้นรวม</span>
            <span class="ps-val">฿{{ fmtBaht(totalPrincipal) }}</span>
          </div>
          <div class="ps-divider"></div>
          <div class="ps-breakdown">
            <div class="ps-item ps-high">
              <span class="ps-dot"></span>
              <span class="ps-item-lbl">เสี่ยงสูง</span>
              <span class="ps-item-val">฿{{ fmtBaht(highPrincipal) }}</span>
              <span class="ps-item-pct">{{ totalPrincipal > 0 ? Math.round(highPrincipal / totalPrincipal * 100) : 0 }}%</span>
            </div>
            <div class="ps-item ps-mid">
              <span class="ps-dot"></span>
              <span class="ps-item-lbl">ปานกลาง</span>
              <span class="ps-item-val">฿{{ fmtBaht(midPrincipal) }}</span>
              <span class="ps-item-pct">{{ totalPrincipal > 0 ? Math.round(midPrincipal / totalPrincipal * 100) : 0 }}%</span>
            </div>
            <div class="ps-item ps-low">
              <span class="ps-dot"></span>
              <span class="ps-item-lbl">ต่ำ</span>
              <span class="ps-item-val">฿{{ fmtBaht(lowPrincipal) }}</span>
              <span class="ps-item-pct">{{ totalPrincipal > 0 ? Math.round(lowPrincipal / totalPrincipal * 100) : 0 }}%</span>
            </div>
          </div>
          <div class="ps-bar">
            <div class="ps-bar-high" :style="{ width: totalPrincipal > 0 ? (highPrincipal / totalPrincipal * 100) + '%' : '0%' }"></div>
            <div class="ps-bar-mid"  :style="{ width: totalPrincipal > 0 ? (midPrincipal  / totalPrincipal * 100) + '%' : '0%' }"></div>
            <div class="ps-bar-low"  :style="{ width: totalPrincipal > 0 ? (lowPrincipal  / totalPrincipal * 100) + '%' : '0%' }"></div>
          </div>
        </div>

        <div ref="scrollBox" class="table-wrap">
          <table>
            <thead>
              <tr>
                <th style="width:48px" class="c">#</th>
                <th>ลูกค้า</th>
                <th class="c col-ticket">เลขตั๋ว</th>
                <th class="l col-type">ประเภท</th>
                <th class="r col-due">วันครบ</th>
                <th class="r col-days">เหลือ (วัน)</th>
                <th class="r col-principal">เงินต้น (฿)</th>
                <th class="r col-prob">
                  % โอกาสหลุด
                  <button class="prob-info-btn" title="คำอธิบาย % โอกาสหลุด" @click.stop="showProbModal = true">ℹ</button>
                </th>
                <th class="c col-level">ระดับเสี่ยง</th>
                <th class="r col-extra">ประวัติหลุด</th>
                <th class="r col-extra">Risk Score</th>
                <th class="r col-extra">DMI</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in visibleItems" :key="row.rank"
                :class="['row-' + row.riskLevel.toLowerCase(), row.rank <= 3 ? 'top3' : '']"
                style="cursor:pointer"
                @click="handleRowClick($event, row)">
                <!-- rank -->
                <td class="c">
                  <span :class="['rank', rankClass(row.rank)]">{{ row.rank }}</span>
                </td>
                <!-- customer -->
                <td>
                  <span class="cust-link">{{ row.customerName || row.customerId }}</span>
                </td>
                <!-- ticket no -->
                <td class="c">
                  <button v-if="row.ticketNo" 
                          type="button"
                          class="ticket-no" 
                          style="background: none; border: none; padding: 0; margin: 0; color: #334155; font-family: monospace; font-size: .82rem; text-decoration: underline; cursor: pointer; display: inline;"
                          @click.stop.prevent="openDetail(row)">
                          {{ row.ticketNo }}
                  </button>
                  <span v-else class="tag-muted">-</span>
                </td>
                <!-- asset type -->
                <td>
                  <span v-if="row.assetType" class="asset-type-tag">{{ row.assetType }}</span>
                  <span v-else class="tag-muted">—</span>
                </td>
                <!-- due date -->
                <td class="r">{{ fmtDate(row.dueDate) }}<span v-if="row.phlatStatus" class="phlat-tag"> (ผลัด)</span></td>
                <!-- days remaining -->
                <td class="r">
                  <span :class="daysClass(row.daysRemaining)">
                    {{ row.daysRemaining < 0 ? `เกินกำหนด(${Math.abs(row.daysRemaining)})` : row.daysRemaining }}
                  </span>
                </td>
                <!-- principal -->
                <td class="r"><span class="metric-main">{{ fmtBaht(row.principal) }}</span></td>
                <!-- probability bar + pct -->
                <td class="r">
                  <div class="prob-wrap">
                    <div class="prob-bar-bg">
                      <div class="prob-bar-fill" :style="probBarStyle(row)"></div>
                    </div>
                    <span :class="['prob-pct', probClass(row.riskLevel)]">
                      {{ row.forfeitProbabilityPct.toFixed(1) }}%
                    </span>
                  </div>
                </td>
                <!-- risk level badge -->
                <td class="c">
                  <span :class="['risk-badge', 'risk-' + row.riskLevel.toLowerCase()]">
                    {{ riskLabel(row.riskLevel) }}
                  </span>
                </td>
                <!-- breakdown: hist forfeit rate -->
                <td class="r col-extra">
                  <span :class="row.histForfeitRatePct > 15 ? 'tag-red' : row.histForfeitRatePct > 5 ? 'tag-amber' : 'tag-green'">
                    {{ row.histForfeitRatePct.toFixed(1) }}%
                  </span>
                </td>
                <!-- breakdown: risk score -->
                <td class="r col-extra">
                  <span :class="['score-chip', scoreChipClass(row.riskScore)]">
                    {{ row.riskScore.toFixed(2) }}
                  </span>
                </td>
                <!-- breakdown: dmi -->
                <td class="r col-extra">
                  <span :class="['dmi-chip', dmiChipClass(row.dmi)]">
                    {{ row.dmi != null ? row.dmi.toFixed(2) : '—' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Legend -->
      <div class="legend-card">
        <div class="legend-title">วิธีคำนวณ % ความน่าจะหลุด</div>
        <div class="legend-items">
          <div class="legend-item">
            <span class="legend-dot" style="background:#3b82f6"></span>
            <span><b>ประวัติหลุด 40%</b> — สัดส่วนเงินต้นที่เคยหลุดต่อเงินต้นที่ปิดแล้วทั้งหมด</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background:#8b5cf6"></span>
            <span><b>Risk Score 35%</b> — คะแนนความเสี่ยงรวม (ความตรงเวลา, พฤติกรรมต่อดอก, ฯลฯ)</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background:#f59e0b"></span>
            <span><b>Overdue ปัจจุบัน 15%</b> — มีตั๋วเกินกำหนดอยู่แล้วหรือไม่</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background:#ef4444"></span>
            <span><b>ความเร่งด่วน 10%</b> — ยิ่งใกล้ due date ยิ่งให้น้ำหนักสูง</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background:#10b981"></span>
            <span><b>Risk Score</b> — คะแนนความเสี่ยงลูกค้า (สูง = น่าเชื่อถือ, ต่ำ = เสี่ยง)</span>
          </div>
          <div class="legend-item">
            <span class="legend-dot" style="background:#6366f1"></span>
            <span><b>DMI (Data Maturity Index)</b> — ดัชนีความสมบูรณ์ของข้อมูลประวัติลูกค้า คำนวณจาก 3 ปัจจัย: จำนวน chain, ช่วงเวลาที่มีข้อมูล, และความหลากหลายของประเภทธุรกรรม (≥70 = ข้อมูลน่าเชื่อถือสูง, 40–69 = ปานกลาง, &lt;40 = ข้อมูลน้อย; — = ยังไม่มีประวัติ)</span>
          </div>
        </div>
      </div>
    </template>

  <!-- Forfeit Probability Explanation Modal -->
  <Teleport to="body">
    <div v-if="showProbModal" class="prob-modal-overlay" @click.self="showProbModal = false">
      <div class="prob-modal">
        <div class="prob-modal-hdr">
          <div class="prob-modal-title">% โอกาสหลุด — คืออะไร?</div>
          <button class="prob-modal-close" @click="showProbModal = false">✕</button>
        </div>
        <div class="prob-modal-body">
          <p class="prob-modal-lead">
            ค่าที่แสดงคือ <strong>ความน่าจะเป็นที่ตั๋วใบนี้จะ "หลุด" (ทรัพย์หลุดจำนำ)</strong> โดยไม่ได้ถูกไถ่ถอน
            คำนวณจาก 4 ปัจจัยของลูกค้าและสถานะตั๋ว รวมเป็น <em>คะแนนรวม 0–100%</em>
          </p>
          <div class="prob-modal-formula">
            <span class="prob-formula-label">สูตร:</span>
            <code>P(หลุด) = P1×40% + P2×35% + P3×15% + P4×10%</code>
          </div>
          <div class="prob-modal-factors">
            <div class="pmf-row">
              <div class="pmf-badge pmf-p1">P1</div>
              <div class="pmf-content">
                <div class="pmf-name">ประวัติหลุด <span class="pmf-weight">น้ำหนัก 40%</span></div>
                <div class="pmf-desc">อัตราหลุดในอดีตของลูกค้า — คำนวณจากสัดส่วนตั๋วที่เคยหลุดต่อตั๋วที่ปิดแล้วทั้งหมด (×2, สูงสุด 100%) หากไม่มีประวัติ default = 30%</div>
              </div>
            </div>
            <div class="pmf-row">
              <div class="pmf-badge pmf-p2">P2</div>
              <div class="pmf-content">
                <div class="pmf-name">Risk Score ลูกค้า <span class="pmf-weight">น้ำหนัก 35%</span></div>
                <div class="pmf-desc">ความเสี่ยงรวมของลูกค้า — Risk Score ต่ำ = ความเสี่ยงสูง (P2 = 1 − riskFinal/100)</div>
              </div>
            </div>
            <div class="pmf-row">
              <div class="pmf-badge pmf-p3">P3</div>
              <div class="pmf-content">
                <div class="pmf-name">ค้างชำระปัจจุบัน <span class="pmf-weight">น้ำหนัก 15%</span></div>
                <div class="pmf-desc">จำนวนวันที่เกินกำหนดมากที่สุดในบรรดาตั๋วเปิดอยู่ทั้งหมด — 60 วันขึ้นไป = สูงสุด (ถ้าไม่มีตั๋วค้างชำระ = 0)</div>
              </div>
            </div>
            <div class="pmf-row" style="border-bottom:none">
              <div class="pmf-badge pmf-p4">P4</div>
              <div class="pmf-content">
                <div class="pmf-name">ความเร่งด่วนของตั๋วใบนี้ <span class="pmf-weight">น้ำหนัก 10%</span></div>
                <div class="pmf-desc">วันที่เหลือก่อนครบกำหนดของตั๋วใบนี้โดยเฉพาะ — ครบกำหนดวันนี้ = 100%, ยังเหลือ 20 วันขึ้นไป = 0%</div>
              </div>
            </div>
          </div>
          <div class="prob-modal-levels">
            <div class="pml-title">เกณฑ์ระดับความเสี่ยง</div>
            <div class="pml-row"><span class="pml-dot pml-high"></span><span class="pml-label">สูง (HIGH)</span><span class="pml-range">≥ 65%</span></div>
            <div class="pml-row"><span class="pml-dot pml-mid"></span><span class="pml-label">ปานกลาง (MEDIUM)</span><span class="pml-range">35% – 64%</span></div>
            <div class="pml-row"><span class="pml-dot pml-low"></span><span class="pml-label">ต่ำ (LOW)</span><span class="pml-range">&lt; 35%</span></div>
          </div>
          <p class="prob-modal-note">หมายเหตุ: P1–P3 ใช้ข้อมูลระดับลูกค้าเหมือนกันทุกแถว · P4 เปลี่ยนตามวันครบกำหนดของแต่ละตั๋ว</p>
        </div>
      </div>
    </div>
  </Teleport>

    <!-- Detail Modal -->
  <TicketDetailModal 
  v-if="workcaseDetail !== null"
  :workcaseDetail="workcaseDetail"
  :images="images"
  :listinfo="listinfo"
  :paymentE="paymentE"
  :paymentL="payment_l"
  :promotionDetail="promotionDetail"
  :depRef="dep_ref"
  :tkStatus="tk_status"
  :intCal="int_cal"
  :intPeriod="int_period"
  :transactionLabel="checkTransaction(workcaseDetail.transactionType)"
  :history="'no'" 
  v-model:fcusdt="fcusdt" 
  :afChangename=af_changename
  :bfChangename=bf_changename
  :transDatePrv=transdate_prv
  @copy="copyBtn"
  @openHistory="NewTab"/>

  <!-- วางไว้ท้ายไฟล์นอกตาราง -->
  <material-button color="info" variant="outline" class="rounded-circle" v-show="false"
                  data-bs-toggle="modal" data-bs-target="#lookupModal2" id="detailModal">
  </material-button>

  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useScoreStore } from '@/stores/score.js'
import { withOrigin } from '@/utils/detailNavigation.js'
import { useRestorableView } from '@/composables/useRestorableView.js'
import { forecastApi } from '@/utils/scoreSvcApiNew.js'
import TicketDetailModal from '@/components/TicketDetailModal.vue';
import { VueImageZoomer } from 'vue-image-zoomer'
import 'vue-image-zoomer/dist/style.css';
import MaterialButton from "@/components/MaterialButton.vue"
import moment from 'moment'
import noImg from "@/assets/img/noimg.png"

import { useWorkCaseStore } from '@/stores/workcase'
import { useInterestStore } from '@/stores/interest'
import { usePaymentGroupStore } from '@/stores/paymentgroup'
import { useDocumentStore } from '@/stores/document'
import { useDepositStore } from '@/stores/deposit'
import { useCashAccountStore } from '@/stores/cashaccount'

const route  = useRoute()
const router = useRouter()
const store = useScoreStore()
const storewc = useWorkCaseStore()
const storei = useInterestStore()
const storep = usePaymentGroupStore()
const stored = useDocumentStore()
const storedepo = useDepositStore()
const storeca = useCashAccountStore()


const dayOptions   = [7, 15, 30]
const limitOptions = [50, 100, 200]
const days        = ref(15)
const limit       = ref(100)
const items       = ref([])
const loading     = ref(false)
const error       = ref(null)
const hideOverdue  = ref(false)
const filterType   = ref('')
const filterPhlat  = ref('')
const showProbModal = ref(false)
let loadRequestId = 0
let inFlightKey = null

const workcaseDetail = ref(null);
const tk_status = ref('');
const int_cal = ref(0);
const paymentE = ref('');
const images = ref([])
const images_l = ref([])
const payment_l = ref([])
const int_period = ref('');
const fcusdt = ref(false);
const dep_ref = ref('');
const promotionDetail = ref(null);
const bf_changename = ref('');
const af_changename = ref('');
const transdate_prv = ref(null);
const listinfo = ref([]);
const shop_detail = ref(null)



const getshopInfo = async (val) => {
  shop_detail.value = null
  const response = await storeca.cashaccountInfoFromSessionFetch();
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        shop_detail.value = response.data.data
      }
    }
  }
}

getshopInfo()

const handleRowClick = (event, row) => {
  // 1. ดึงเป้าหมายที่โดนเมาส์จิ้มจริงๆ ขึ้นมาดู
  const target = event.target;

  // 2. เช็กอย่างละเอียดว่าสิ่งที่จิ้ม คือ ตัวลิงก์เลขตั๋ว (.ticket-no) หรือไม่
  if (target && (target.classList.contains('ticket-no') || target.closest('.ticket-no'))) {
    // สั่งบล็อกและทำลายอีเวนต์การคลิกทุกรูปแบบของเบราว์เซอร์ทิ้งทันที!
    event.stopPropagation();
    event.stopImmediatePropagation(); // ปิดสิทธิ์ไม่ให้โค้ดส่วนอื่นในหน้านี้รับรู้อีเวนต์นี้เด็ดขาด
    event.preventDefault();
    
    // เรียกฟังก์ชันเปิดรายละเอียดตั๋วของคุณ
    openDetail(row);
    return; // จบการทำงานตรงนี้ทันที ไม่ให้ไหลไปโดนคำสั่งเปลี่ยนหน้าด้านล่าง
  }

  // 3. โค้ดเดิมของคนอื่น (จะทำงานเฉพาะตอนที่ผู้ใช้ไม่ได้กดเลขตั๋ว)
  openPortfolio(row)
};

// Same round-trip problem as CsTop100: a row click goes to CsPortfolio, whose "← กลับ" is
// router.go(-1), which remounts this page. Filters ride in the query string and the scroll offsets
// in sessionStorage, so that lands back on the list as it was left. It seeds the refs above
// synchronously, ahead of the watch/onMounted loaders below, so they load once with the restored
// days/limit. filterType is left unvalidated on purpose: its options come from whatever asset types
// the data happens to hold, which isn't known until the rows arrive.
const { scrollBox } = useRestorableView({
  routeName: 'CsForecast',
  filters: {
    days:  { ref: days,        default: 15,    values: dayOptions },
    limit: { ref: limit,       default: 100,   values: limitOptions },
    hide:  { ref: hideOverdue, default: false },
    type:  { ref: filterType,  default: '' },
    phlat: { ref: filterPhlat, default: '',    values: ['yes', 'no'] },
  },
  // The table is behind a v-else chain, so rows exist in the DOM only once loading is done and
  // something survives the filters.
  ready: () => !loading.value && visibleItems.value.length > 0,
})

const assetTypeOptions = computed(() => {
  const types = new Set(items.value.map(r => r.assetType).filter(Boolean))
  return [...types].sort()
})

const visibleItems = computed(() => {
  let list = items.value
  if (hideOverdue.value)          list = list.filter(r => r.daysRemaining > 0)
  if (filterType.value)           list = list.filter(r => r.assetType === filterType.value)
  if (filterPhlat.value === 'yes') list = list.filter(r => r.phlatStatus)
  if (filterPhlat.value === 'no')  list = list.filter(r => !r.phlatStatus)
  return list
})
const forecastStats = computed(() => {
  const stats = {
    highCount: 0,
    midCount: 0,
    totalPrincipal: 0,
    highPrincipal: 0,
    midPrincipal: 0,
    lowPrincipal: 0,
  }
  for (const row of visibleItems.value) {
    const principal = row.principal || 0
    stats.totalPrincipal += principal
    if (row.riskLevel === 'HIGH') {
      stats.highCount += 1
      stats.highPrincipal += principal
    } else if (row.riskLevel === 'MEDIUM') {
      stats.midCount += 1
      stats.midPrincipal += principal
    } else {
      stats.lowPrincipal += principal
    }
  }
  return stats
})
const highCount      = computed(() => forecastStats.value.highCount)
const midCount       = computed(() => forecastStats.value.midCount)
const totalPrincipal = computed(() => forecastStats.value.totalPrincipal)
const highPrincipal  = computed(() => forecastStats.value.highPrincipal)
const midPrincipal   = computed(() => forecastStats.value.midPrincipal)
const lowPrincipal   = computed(() => forecastStats.value.lowPrincipal)

async function load() {
  if (!store.hasData) return
  // Same de-dup key as before, just built from the request params instead of a query string.
  const key = JSON.stringify({ ...store.scope(), days: days.value, limit: limit.value })
  if (inFlightKey === key) return
  const requestId = ++loadRequestId
  inFlightKey = key
  loading.value = true
  error.value   = null
  try {
    const data = await forecastApi.expiring(days.value, limit.value)
    if (requestId === loadRequestId) items.value = data
  } catch (e) {
    if (requestId === loadRequestId) error.value = e.message
  } finally {
    if (inFlightKey === key) inFlightKey = null
    if (requestId === loadRequestId) loading.value = false
  }
}

function setDays(d) {
  days.value = d
}

watch([days, limit], load)
onMounted(() => {
  // Every other credit-score page initialises the store itself; this one assumed someone already
  // had — which held while it was reached by clicking through, and broke the moment it was opened
  // or refreshed on its own: nothing filled the store, hasData stayed false, and the page sat on
  // "ยังไม่มีข้อมูล" with a healthy dataset on the server. (App.vue does not init it, despite what
  // the note here used to claim.)
  store.init()
  load()
})
// init() is async, and load() bails while the store is empty; run it again once the data lands.
watch(() => store.hasData, (ready) => { if (ready && !items.value.length) load() })

// ── Formatting ─────────────────────────────────────────────────────────────

function fmtBaht(v) {
  return Number(v).toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
}

function fmtDate(d) {
  if (!d) return '—'
  try {
    const dt = new Date(d + 'T00:00:00')
    return dt.toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' })
  } catch { return d }
}

// ── CSS helpers ────────────────────────────────────────────────────────────

function rankClass(r) {
  if (r === 1) return 'r1'
  if (r === 2) return 'r2'
  if (r === 3) return 'r3'
  return 'rn'
}

function daysClass(d) {
  if (d <= 0)  return 'tag-red'
  if (d <= 7)  return 'tag-amber'
  return 'tag-green'
}

function probClass(level) {
  if (level === 'HIGH')   return 'prob-high'
  if (level === 'MEDIUM') return 'prob-mid'
  return 'prob-low'
}

function probBarStyle(row) {
  const color = row.riskLevel === 'HIGH'   ? '#dc2626'
              : row.riskLevel === 'MEDIUM' ? '#d97706'
              : '#059669'
  return { width: row.forfeitProbabilityPct + '%', background: color }
}

function riskLabel(level) {
  if (level === 'HIGH')   return 'สูง'
  if (level === 'MEDIUM') return 'ปานกลาง'
  return 'ต่ำ'
}

function scoreChipClass(score) {
  if (score >= 67) return 'chip-high'
  if (score >= 34) return 'chip-mid'
  return 'chip-low'
}

function dmiChipClass(dmi) {
  if (dmi == null) return 'dmi-na'
  if (dmi >= 70) return 'dmi-high'
  if (dmi >= 40) return 'dmi-mid'
  return 'dmi-low'
}

function printPage() {
  const win = window.open('', '_blank')
  if (!win) return

  const filterMeta = [
    `ดูล่วงหน้า ${days.value} วัน`,
    `${visibleItems.value.length} ตั๋ว`,
    filterType.value  ? `ประเภท: ${filterType.value}` : '',
    filterPhlat.value === 'yes' ? 'ตั๋วผลัดเท่านั้น' : '',
    filterPhlat.value === 'no'  ? 'ไม่ผลัดเท่านั้น'  : '',
    hideOverdue.value ? 'ซ่อนตั๋วเกินกำหนด' : '',
  ].filter(Boolean).join(' · ')

  const rows = visibleItems.value.map((row, i) => {
    const daysCell = row.daysRemaining < 0
      ? `เกินกำหนด(${Math.abs(row.daysRemaining)})`
      : String(row.daysRemaining)
    return `<tr>
      <td class="c">${i + 1}</td>
      <td>${row.customerName || row.customerId || ''}</td>
      <td class="c">${row.ticketNo || '—'}</td>
      <td>${row.assetType || '—'}</td>
      <td>${fmtDate(row.dueDate)}${row.phlatStatus ? ' (ผลัด)' : ''}</td>
      <td class="r">${daysCell}</td>
      <td class="r">${fmtBaht(row.principal)}</td>
      <td class="r">${row.forfeitProbabilityPct.toFixed(1)}%</td>
      <td>${riskLabel(row.riskLevel)}</td>
      <td class="r">${row.histForfeitRatePct.toFixed(1)}%</td>
      <td class="r">${row.riskScore.toFixed(2)}</td>
      <td class="r">${row.dmi != null ? row.dmi.toFixed(2) : '—'}</td>
    </tr>`
  }).join('')

  win.document.write(`<!DOCTYPE html>
<html lang="th"><head>
<meta charset="UTF-8">
<title>รายการตั๋วที่คาดว่าเสี่ยงหลุด</title>
<link href="https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700&display=swap" rel="stylesheet">
<style>
  @page { size: landscape; margin: 0; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Sarabun', 'Tahoma', sans-serif; font-size: 9pt; color: #000; background: #fff; padding: 10mm 12mm; }
  .print-header { text-align: center; padding: 8px 0 12px; }
  .ph-title { font-size: 14pt; font-weight: 700; margin-bottom: 4px; }
  .ph-meta  { font-size: 8pt; color: #444; margin-bottom: 3px; }
  .ph-principal { font-size: 8pt; font-weight: 600; }
  table { border-collapse: collapse; width: 100%; font-size: 8pt; }
  th { border: 1px solid #000; padding: 4px 6px; font-weight: 700; text-align: left; white-space: nowrap; }
  td { border: 1px solid #000; padding: 3px 6px; }
  .r { text-align: right; }
  .c { text-align: center; }
  tr { page-break-inside: avoid; }
</style>
</head><body>
<div class="print-header">
  <div class="ph-title">รายการตั๋วที่คาดว่าเสี่ยงหลุด</div>
  <div class="ph-meta">${filterMeta}</div>
  <div class="ph-principal">เงินต้นรวม ฿${fmtBaht(totalPrincipal.value)} &nbsp;·&nbsp; เสี่ยงสูง ฿${fmtBaht(highPrincipal.value)} &nbsp;·&nbsp; ปานกลาง ฿${fmtBaht(midPrincipal.value)} &nbsp;·&nbsp; ต่ำ ฿${fmtBaht(lowPrincipal.value)}</div>
</div>
<table>
  <thead><tr>
    <th class="c" style="width:32px">#</th>
    <th>ลูกค้า</th><th class="c">เลขตั๋ว</th><th>ประเภท</th><th>วันครบ</th>
    <th class="r">เหลือ (วัน)</th><th class="r">เงินต้น (฿)</th>
    <th class="r">% โอกาสหลุด</th><th>ระดับเสี่ยง</th>
    <th class="r">ประวัติหลุด</th><th class="r">Risk Score</th><th class="r">DMI</th>
  </tr></thead>
  <tbody>${rows}</tbody>
</table>
</body></html>`)
  win.document.close()
  win.focus()
  setTimeout(() => { win.print() }, 600)
}

// Both of the detail pages' inputs ride in history.state, which keeps their URL down to a bare path
// and survives a reload: the customer they show, and — via withOrigin — the list we are leaving, so
// their back buttons return here rather than unwinding the history stack a step at a time.
// selectedCustomerId is in-memory only and is set here just for the pages that still read it.
function openPortfolio(row) {
  const id = customerRouteId(row)
  store.selectedCustomerId = id
  router.push({ name: 'CsPortfolio', state: withOrigin(route, { cus: id }) })
}

function customerRouteId(row) {
  return store.routeCustomerId(row)
}

async function openDetail(item) {
  workcaseDetail.value = null
  images.value = []
  images_l.value = []
  listinfo.value = []
  tk_status.value = ''
  int_cal.value = 0
  paymentE.value = ''
  int_period.value = ''
  fcusdt.value = false
  dep_ref.value = ''
  bf_changename.value = ''
  af_changename.value = ''
  transdate_prv.value = null
  promotionDetail.value = null

  await getworkCaseInfo(item.ticketNo)
  if (workcaseDetail.value != null) {

    if (workcaseDetail.value.changeName ) {
      af_changename.value = item.cusName
      getworkCaseInfoPrv(workcaseDetail.value.prevRefNumber)
    }else { 
      if (workcaseDetail.value.transactionType !== 'PAWN') { 
          getworkCaseInfoPrv(workcaseDetail.value.prevRefNumber)
      }
    }

    if (workcaseDetail.value.deposit) {
      getDepositInfo(workcaseDetail.value.tkRefNumber)
    }
    detailPayment(workcaseDetail.value.paymentGroupUid)

    images_l.value = workcaseDetail.value.ticket.images
    listinfo.value = workcaseDetail.value.ticket.collaterals

    if (workcaseDetail.value.promotionUid !== '0') { 
          retrieveInfo(workcaseDetail.value)
    }

    if (workcaseDetail.value.transactionType !== 'REDEEM') {
      if(workcaseDetail.value.latest){
        calIntInt()
      }
      if (workcaseDetail.value.status === 'FINISH') {
        tk_status.value = 'ปกติ'
      } else if (workcaseDetail.value.status === 'FREEZE') {
        tk_status.value = 'อายัด'
      } else if (workcaseDetail.value.status === 'TERMINATE') {
        tk_status.value = 'ยกเลิก'
      } else if (workcaseDetail.value.status === 'ACTIVE') {
        tk_status.value = 'กำลังดำเนินการ'
      } else if (workcaseDetail.value.status === 'BREAKING') {
        tk_status.value = 'ของหลุด'
      }

    } else {
      if (workcaseDetail.value.status === 'FINISH') {
        tk_status.value = 'ไถ่ถอน'
      } else if (workcaseDetail.value.status === 'TERMINATE') {
        tk_status.value = 'ยกเลิก'
      }
    }
    // 🟢 1. ตรวจสอบรายการไฟล์แนบจาก images_l.value
    if (images_l.value.length > 0) {
      
      // 🟢 ไฮไลท์จุดแก้: ต้องมี `await` เพื่อบังคับรอให้ Promise.all โหลดล้างหัวไฟล์เสร็จก่อน
      await getTicketImage();
      
      // 🟢 2. ย้ายคำสั่งส่ง MouseEvent มาดักเปิด Modal เฉพาะในกรณีที่เป็นรูปภาพจริงหรือกรณี noImg เท่านั้น
      // เพื่อเปิดทางให้ไฟล์ PDF (ดีดแท็บใหม่) และ Excel (ดาวน์โหลด) วิ่งผ่านไปได้โดยไม่หลุดเข้า Modal รูปแตก
      if (images.value.length > 0) {
        const targetFile = images.value[0];
        const fileType = targetFile.title || "";
        
        // ถ้าไม่ใช่ PDF และไม่ใช่ Excel แปลว่าเป็นรูปภาพชัวร์ หรือเป็นแถวภาพ noImg
        if (fileType !== 'application/pdf' && !fileType.includes('spreadsheetml') && !fileType.includes('excel')) {
          await nextTick();
          const modalBtn = document.getElementById('detailModal');
          if (modalBtn) {
            const clickEvent = new MouseEvent('click', {
              bubbles: false, // 🔥 ห้ามวิ่งทะลุขึ้นไปหาแท็กพ่อ <tr>
              cancelable: true
            });
            modalBtn.dispatchEvent(clickEvent);
          }
        }
      }
      
    } else {
      // 🟢 3. แผนสำรอง: กรณีที่ Record นี้ไม่มี Uid ไฟล์ผูกมาเลยแต่แรก
      let obj = {
        src: noImg,
        thumbnailImageSrc: noImg,
        alt: '',
        title: ''
      }
      images.value.push(obj)
      
      await nextTick();
      const modalBtn = document.getElementById('detailModal');
      if (modalBtn) {
        const clickEvent = new MouseEvent('click', {
          bubbles: false, // 🔥 ห้ามวิ่งทะลุขึ้นไปหาแท็กพ่อ <tr>
          cancelable: true
        });
        modalBtn.dispatchEvent(clickEvent);
      }
    }
  }
}

async function getTicketImage() {
  images.value = []
  loading.value = true 

  // 1. กรองเอาเฉพาะรายการที่มี imageUid จริงๆ (ไม่เป็น "0") มารวมกลุ่มไว้
  const validFiles = images_l.value.filter(file => file.imageUid && file.imageUid !== "0");

  if (validFiles.length > 0) {
    // 🟢 2. วนลูปสร้าง Requests ในรูปแบบ POST Payload ส่งเข้าหา Store ส่วนกลาง
    const requests = validFiles.map((fileItem) => {
      const payload = {
        groupUid: shop_detail.value.groupUid, // ส่งค่าว่าง หรือดึงจากตัวแปร groupUid ของหน้านี้ (ถ้ามี)
        shopUid: shop_detail.value.uid, // 🟢 ดึงจาก key.uid ตามสเปก Payload ดั้งเดิมของหน้านี้
        uid: String(fileItem.imageUid) // รหัสไอดีของไฟล์แนบ
      };
      // เรียกใช้ผ่านฟังก์ชัน store ส่วนกลาง (ตรวจสอบชื่อตัวแปร store หน้านี้ให้ตรงนะครับ เช่น stored หรือ storedoc)
      return stored.getFileFetch(payload); 
    });

    try {
      // 🟢 3. เปลี่ยนมาใช้ Promise.all ยิงดึงทุกไฟล์พร้อมกันขนานกันรวดเดียว [INDEX]
      const allResults = await Promise.all(requests);
      
      if (allResults) {
        for (let i = 0; i < allResults.length; i++) {
          if (allResults[i] && allResults[i].status === 200 && allResults[i].data !== null) {
            const rawData = allResults[i].data;
            const isBlob = rawData instanceof Blob || (rawData && rawData.size !== undefined);
            
            if (isBlob) {
              // สแกนตรวจสอบเนื้อไฟล์จริง (Magic Numbers)
              const textHead = await rawData.slice(0, 4).text();
              let cleanBlob;
              let fileType;

              if (textHead === "%PDF") {
                cleanBlob = new Blob([rawData], { type: 'application/pdf' });
                fileType = 'application/pdf';
              } else if (textHead.startsWith("PK")) {
                cleanBlob = new Blob([rawData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
              } else {
                // 🖼️ สลัดหัวดาวน์โหลดทิ้ง บังคับครอบหัวภาพใหม่ให้สะอาด 100% บนหน้าบ้าน
                cleanBlob = new Blob([rawData], { type: 'image/jpeg' });
                fileType = 'image/jpeg';
              }

              const dataUrl = URL.createObjectURL(cleanBlob);
              const originalItem = validFiles[i];

              images.value.push({
                src: dataUrl,
                thumbnailImageSrc: dataUrl,
                alt: '',
                title: fileType, 
                filename: 'attachment_' + originalItem.imageUid, 
                uid: originalItem.uid,
                imageUid: originalItem.imageUid,
                rawBlob: cleanBlob 
              });
            }
          }
        }
      }
    } catch (e) {
      console.error("Error in Promise.all getTicketImage:", e);
      loading.value = false;
      return;
    }
  }

  // 🟢 4. การจัดการปิด Loading หรือสวมกรณีไม่มีรูปภาพ (คงลอจิกเดิมหน้านี้ไว้)
  if (images.value.length > 0) {
    loading.value = false;
  } else {
    // แผนสำรอง: ถ้าไม่มีรูปแนบอยู่จริง ให้ยัดรูปภาพทดแทน (No Image) ตามเงื่อนไขเดิมของคุณ
    images.value.push({
      src: noImg,
      thumbnailImageSrc: noImg,
      alt: '',
      title: ''
    });
    loading.value = false;
  }
}



async function getworkCaseInfo(refNumber) {
  let data = "";
  data = {
    groupUid: shop_detail.value.groupUid,
    shopUid: shop_detail.value.uid,
    refNumber: refNumber,
  };
  const payload = data;
  // console.log("workCaseInfo p : " + JSON.stringify(payload));
  const response = await storewc.workCaseInfoByRefFetch(payload);
  // console.log("getworkCaseInfo : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        workcaseDetail.value = response.data.data
      }
    }
  }
}

async function getworkCaseInfoPrv(prevRefNumber) {
  let workcase_prv = null
  let data = "";
  data = {
    groupUid: shop_detail.value.groupUid,
    shopUid: shop_detail.value.uid,
    refNumber: prevRefNumber,
  };
  const payload = data;

  const response = await storewc.workCaseInfoByRefFetch(payload);
  //  console.log("workCaseInfoByRef : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        workcase_prv = response.data.data
        bf_changename.value = workcase_prv.tkCustomerInfo.name 
        transdate_prv.value = workcase_prv.workItems[0].startTime
      }
    }
  }
}


async function getDepositInfo(tkRef) {
  let data = "";
  data = {
    index: 0,
    size: 1,
    groupUid: shop_detail.value.groupUid,
    shopUid: shop_detail.value.uid,
    cusUid: '',
    refNumber: '',
    tkRefNumber: tkRef.trim(),
    channel: '',
    txDate: 0,
  };
  const payload = data;
  // console.log("ld p : " + JSON.stringify(payload));
  const response = await storedepo.listDepositFetch(payload);
  // console.log("ld r : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        let listd = response.data.data.list
        dep_ref.value = listd[0].refNumber
      }
    }
  }
}

async function retrieveInfo(item) {
  promotionDetail.value = null
  let data = "";
  let pawnValue = 0
  if (item.transactionType != 'REDEEM') {
      pawnValue = item.newPawnValue
  } else {
      pawnValue = item.pawnValue
  }

  let cusUid = item.customerInfo.customerUid

  if(item.represent || item.shopRepresent){
     cusUid =  item.tkCustomerInfo.customerUid 
  }

  data = {
    cusUid : cusUid,
    pawnDate: moment(item.workItems[0].startTime).format('YYYY-MM-DD'),
    previousTxDate: '',
    pawnValue: pawnValue,
  };
  const payload = data;
  // console.log("calIntInt p : " + JSON.stringify(payload));
  const response = await storei.interestRetrieveFetch(payload);
  // console.log("retrieveInfo r : " + JSON.stringify(response.data));
  let res = ''
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        res = response.data.data
        const result = res.interests.flatMap(item => {
        // เงื่อนไข: เป็นโปรโมชัน และ periodMonths > 1
        if (item.promotion === true && item.periodMonths > 1) {
          // สร้าง Array ใหม่ที่มีจำนวน element เท่ากับ periodMonths และใส่ข้อมูล item ลงไป
          return Array.from({ length: item.periodMonths }, () => ({ ...item }));
        }
        // หากไม่ตรงเงื่อนไข ให้คืนค่าเดิมกลับไป 1 ชุด
        return [item];
        });
        // console.log('result : ' + JSON.stringify(result));
      res.interests  =  result
      promotionDetail.value = res
      // console.log('promotionDetail : ' + JSON.stringify(promotionDetail.value));
      }
    }
  }
}

async function calIntInt() {
  // console.log('workcaseDetail : ' + JSON.stringify(workcaseDetail.value));
  let postponeDate = ''
  if (workcaseDetail.value.postponeCount != 0) {
    postponeDate = moment(workcaseDetail.value.postponeDate).format('YYYY-MM-DD')
  }
  let data = "";
  let pawnValue = 0
  if (workcaseDetail.value.transactionType != 'REDEEM') {
    pawnValue = workcaseDetail.value.newPawnValue
  } else {
    pawnValue = workcaseDetail.value.pawnValue
  }

  let cusUid = workcaseDetail.value.customerInfo.customerUid

  if(workcaseDetail.value.represent || workcaseDetail.value.shopRepresent){
     cusUid =  workcaseDetail.value.tkCustomerInfo.customerUid 
  }
  data = {
    cusUid: cusUid,
    txType: workcaseDetail.value.transactionType,
    pawnDate: moment(workcaseDetail.value.workItems[0].startTime).format('YYYY-MM-DD'),
    previousTxDate: '',
    postponeDate: postponeDate,
    pawnValue: pawnValue,
    promotionUid: workcaseDetail.value.promotionUid,
    interestUid: workcaseDetail.value.interestUid,
  };
  const payload = data;
  // console.log("calIntInt p : " + JSON.stringify(payload));
  const response = await storei.interestCalculateFetch(payload);
  // console.log("calIntInt r : " + JSON.stringify(response.data));
  let res = ''
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        res = response.data.data
        int_cal.value = Number(res.overdueInterest) + Number(res.dueInterest)
      }
    }
  }
}

async function detailPayment(item) {
  // console.log('detailPayment : ' + item);
  let data = "";
  data = {
    uid: item,
  };
  const payload = data;
  const response = await storep.paymentGroupinfoFetch(payload);
  // console.log("detailPayment : " + JSON.stringify(response.data));
  if (response) {
    if (response.status === 200) {
      if (response.data.rtnDesc === "success") {
        paymentE.value = response.data.data
        filterPayType()
        // console.log('detailPayment : ' + JSON.stringify(paymentE.value));
      }
    }
  }
}




function filterPayType() {
  payment_l.value = []
  let pt = [{ name: 'CASH' },
  { name: 'BANK_TRANSFER' },
  { name: 'CREDIT_CARD' },
  { name: 'CHEQUE' },
  { name: 'PROMPT_PAY_MOBILE' },
  { name: 'PROMPT_PAY_TAX_ID' },
  { name: 'QR_CODE' },
  ]
  let lsum = []
  for (let i = 0; i < pt.length; i++) {
    lsum = filterSumPay(pt[i].name)
    if (lsum.length > 0) {
      payment_l.value.push({ name: pt[i].name, pay: lsum[0].pay, receive: lsum[0].receive, fee: lsum[0].fee })
    } else {
      payment_l.value.push({ name: pt[i].name, pay: 0, receive: 0, fee: 0 })
    }
  }
}

function filterSumPay(str) {
  let nump = 0, numr = 0, numf = 0
  let lp = paymentE.value.payments.filter((c) => c.type === str);
  if (lp.length > 0) {
    let pay = 0
    let receive = 0
    let fee = 0
    for (let i = 0; i < lp.length; i++) {
      pay = pay + lp[i].pay
      receive = receive + lp[i].receive
      fee = fee + lp[i].feeReceive
      // fee = fee + lp[i].fee
    }
    nump = pay
    numr = receive
    numf = fee
  }
  let lockL = []
  lockL.push({ pay: nump, receive: numr, fee: numf })
  // console.log('lockL : ' + JSON.stringify(lockL));
  return lockL
}

function checkTransaction(str) {
  // PAWN, INTEREST, REDEEM, INC_PRINCIPLE, DEC_PRINCIPLE
  let side = ''
  if (str === 'PAWN') {
    side = 'จำนำ'
  } else if (str === 'INTEREST') {
    side = 'ต่อดอกเบี้ย'
  } else if (str === 'REDEEM') {
    side = 'ไถ่ถอน'
  } else if (str === 'INC_PRINCIPLE') {
    side = 'เพิ่มต้น'
  } else if (str === 'DEC_PRINCIPLE') {
    side = 'ลดต้น'
  }
  return side
}

function copyBtn() {
  navigator.clipboard.writeText(workcaseDetail.value.tkRefNumber);
}

const NewTab = async () => {
  sessionStorage.removeItem('deferWorkcaseUid');
  sessionStorage.setItem("deferWorkcaseUid", workcaseDetail.value.uid);
  if (sessionStorage.getItem('deferWorkcaseUid') !== null) {
    const win = window.open("#/generals/defer_items", "_blank");
    win.focus();
    sessionStorage.removeItem('deferWorkcaseUid');
  }
}

</script>

<style scoped>
.page-wrap { width: 100%; padding: 16px 24px 48px; display: flex; flex-direction: column; gap: 16px; }

/* ── No data ──────────────────────────────────────────────────────── */
.state-no-data { text-align: center; padding: 80px 24px; color: #6b7280; }
.nd-icon { font-size: 3rem; margin-bottom: 12px; }
.state-no-data h2 { font-size: 1.25rem; font-weight: 700; color: #374151; margin-bottom: 8px; }
.state-no-data p  { font-size: .88rem; margin-bottom: 20px; }
.btn-upload { display: inline-block; background: #1d4ed8; color: #fff; border-radius: 8px; padding: 8px 20px; font-size: .88rem; font-weight: 600; text-decoration: none; }

/* ── Page header ──────────────────────────────────────────────────── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-title-wrap { display: flex; align-items: center; gap: 12px; }
.page-icon  { font-size: 2rem; flex-shrink: 0; }
.page-title { font-size: 1.25rem; font-weight: 800; color: #0f172a; }
.page-subtitle { font-size: .8rem; color: #6b7280; margin-top: 2px; }

/* ── Controls row ─────────────────────────────────────────────────── */
.header-controls { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.days-filter { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.filter-label { font-size: .75rem; color: #6b7280; font-weight: 600; white-space: nowrap; }
.day-btn { padding: 6px 16px; border-radius: 7px; border: 1px solid #e2e6ea; background: #fff; font-size: .82rem; font-weight: 600; color: #374151; cursor: pointer; transition: all .15s; }
.day-btn:hover { border-color: #1d4ed8; color: #1d4ed8; }
.day-btn.active { background: #1d4ed8; border-color: #1d4ed8; color: #fff; box-shadow: 0 2px 6px rgba(29,78,216,.35); }
.toggle-btn { padding: 6px 14px; border-radius: 7px; border: 1px solid #e2e6ea; background: #fff; font-size: .8rem; font-weight: 600; color: #6b7280; cursor: pointer; transition: all .15s; white-space: nowrap; }
.toggle-btn:hover { border-color: #7c3aed; color: #7c3aed; }
.toggle-btn.active { background: #7c3aed; border-color: #7c3aed; color: #fff; box-shadow: 0 2px 6px rgba(124,58,237,.35); }
.type-select { padding: 6px 10px; border-radius: 7px; border: 1px solid #e2e6ea; background: #fff; font-size: .82rem; font-weight: 600; color: #374151; cursor: pointer; outline: none; transition: border-color .15s; }
.type-select:focus { border-color: #1d4ed8; box-shadow: 0 0 0 2px rgba(29,78,216,.1); }

/* ── Summary cards ────────────────────────────────────────────────── */
.summary-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.stat-card { background: #fff; border-radius: 10px; padding: 16px 20px; box-shadow: 0 1px 4px rgba(0,0,0,.07); border-left: 4px solid #e2e6ea; }
.stat-card.stat-high { border-left-color: #dc2626; }
.stat-card.stat-mid  { border-left-color: #d97706; }
.stat-card.stat-principal { border-left-color: #1d4ed8; }
.stat-value { font-size: 1.6rem; font-weight: 800; color: #0f172a; line-height: 1; }
.stat-label { font-size: .72rem; color: #6b7280; margin-top: 4px; font-weight: 600; text-transform: uppercase; letter-spacing: .3px; }
.stat-high .stat-value { color: #dc2626; }
.stat-mid  .stat-value { color: #d97706; }
.stat-principal .stat-value { color: #1d4ed8; }

/* ── Inline states ────────────────────────────────────────────────── */
.state-inline { background: #fff; border-radius: 10px; padding: 40px 20px; text-align: center; font-size: .88rem; color: #6b7280; box-shadow: 0 1px 4px rgba(0,0,0,.07); display: flex; align-items: center; justify-content: center; gap: 10px; }
.state-error { color: #dc2626; }

/* ── Section card ─────────────────────────────────────────────────── */
.section-card { background: #fff; border-radius: 10px; box-shadow: 0 1px 4px rgba(0,0,0,.08), 0 4px 16px rgba(0,0,0,.06); overflow: hidden; }
.section-head { display: flex; align-items: center; gap: 12px; padding: 14px 20px; border-bottom: 1px solid #e2e6ea; flex-wrap: wrap; }

/* ── Principal summary strip ─────────────────────────────────── */
.principal-strip {
  display: flex; align-items: center; gap: 0;
  padding: 10px 20px; background: #f8f9fb;
  border-bottom: 1px solid #e2e6ea; flex-wrap: wrap; gap: 12px;
}
.ps-total { display: flex; flex-direction: column; gap: 1px; min-width: 110px; }
.ps-lbl   { font-size: .65rem; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .4px; }
.ps-val   { font-size: 1rem; font-weight: 800; color: #0f172a; }
.ps-divider { width: 1px; height: 32px; background: #e2e6ea; flex-shrink: 0; }
.ps-breakdown { display: flex; gap: 16px; flex-wrap: wrap; flex: 1; }
.ps-item  { display: flex; align-items: center; gap: 6px; }
.ps-dot   { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.ps-high .ps-dot { background: #dc2626; }
.ps-mid  .ps-dot { background: #d97706; }
.ps-low  .ps-dot { background: #059669; }
.ps-item-lbl { font-size: .75rem; color: #6b7280; font-weight: 600; white-space: nowrap; }
.ps-item-val { font-size: .82rem; font-weight: 700; color: #1a1d23; }
.ps-item-pct { font-size: .72rem; color: #9ca3af; font-weight: 600; }
.ps-high .ps-item-pct { color: #dc2626; }
.ps-mid  .ps-item-pct { color: #d97706; }
.ps-low  .ps-item-pct { color: #059669; }
.ps-bar  { display: flex; width: 100%; height: 4px; border-radius: 2px; overflow: hidden; background: #e5e7eb; }
.ps-bar-high { background: #dc2626; transition: width .3s; }
.ps-bar-mid  { background: #d97706; transition: width .3s; }
.ps-bar-low  { background: #059669; transition: width .3s; }
.section-icon     { font-size: 1.4rem; flex-shrink: 0; }
.section-title    { font-size: 1rem; font-weight: 700; }
.section-subtitle { font-size: .75rem; color: #6b7280; margin-top: 2px; }
.badge-count { background: #f0f2f5; border: 1px solid #e2e6ea; color: #6b7280; border-radius: 12px; padding: 3px 10px; font-size: .72rem; font-weight: 600; white-space: nowrap; }

/* ── Export button ────────────────────────────────────────────── */
.export-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 5px 14px; border-radius: 7px; border: 1.5px solid #1d4ed8;
  background: #fff; color: #1d4ed8; font-size: .78rem; font-weight: 700;
  cursor: pointer; white-space: nowrap; transition: all .15s; flex-shrink: 0;
}
.export-btn:hover { background: #1d4ed8; color: #fff; }

/* ── Print-only header (hidden on screen) ─────────────────────── */
.print-header { display: none; }

/* ── Table ────────────────────────────────────────────────────────── */
.table-wrap { overflow-x: auto; overflow-y: auto; max-height: 1360px; }
table { width: 100%; border-collapse: collapse; line-height: 1.55; }
thead th { position: sticky; top: 0; z-index: 1; background: #f8f9fb; padding: 10px 16px; text-align: left; font-size: .68rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; border-bottom: 1px solid #e2e6ea; }
thead th.r { text-align: right; }
thead th.c { text-align: center; }
thead th.l { text-align: left; }
tbody tr { border-bottom: 1px solid #f3f4f6; transition: background .12s; }
tbody tr:last-child { border-bottom: none; }
tbody tr:hover { background: #f7f9ff !important; }
tbody tr.top3 { background: #fffbf0; }
td { padding: 12px 16px; font-size: .9rem; vertical-align: middle; line-height: 1.55; }
td.r { text-align: right; }
td.c { text-align: center; }

/* Row color tint by risk level */
.row-high { background: #fff5f5; }
.row-medium { background: #fffdf0; }

@media (max-width: 900px) { .col-extra { display: none; } }
@media (max-width: 700px) { .col-ticket, .col-due { display: none; } }

/* ── Rank badge ───────────────────────────────────────────────────── */
.rank { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 7px; font-size: .8rem; font-weight: 800; }
.r1 { background: #fbbf24; color: #78350f; }
.r2 { background: #cbd5e1; color: #334155; }
.r3 { background: #d4a574; color: #7c2d12; }
.rn { background: #f1f5f9; color: #94a3b8; font-size: .75rem; }

/* ── Customer & ticket ────────────────────────────────────────────── */
.cust-link { color: #1d4ed8; font-weight: 600; line-height: 1.55; }
.ticket-no { font-family: monospace; font-size: .82rem; background: #f1f5f9; border-radius: 4px; padding: 1px 6px; color: #334155; }
.asset-type-tag { font-size: .82rem; color: #374151; }
.phlat-tag { font-size: .78rem; color: #5b21b6; font-weight: 600; }

/* ── Days remaining ───────────────────────────────────────────────── */
.tag-red   { color: #dc2626; font-weight: 700; }
.tag-amber { color: #d97706; font-weight: 700; }
.tag-green { color: #059669; font-weight: 600; }
.tag-muted { color: #94a3b8; }

/* ── Probability bar ──────────────────────────────────────────────── */
.prob-wrap { display: flex; align-items: center; gap: 8px; justify-content: flex-end; }
.prob-bar-bg { width: 64px; height: 6px; background: #f3f4f6; border-radius: 3px; overflow: hidden; flex-shrink: 0; }
.prob-bar-fill { height: 100%; border-radius: 3px; transition: width .3s; }
.prob-pct { font-size: .82rem; font-weight: 700; min-width: 40px; text-align: right; }
.prob-high { color: #dc2626; }
.prob-mid  { color: #d97706; }
.prob-low  { color: #059669; }

/* ── Risk badge ───────────────────────────────────────────────────── */
.risk-badge { display: inline-block; padding: 2px 10px; border-radius: 20px; font-size: .75rem; font-weight: 700; white-space: nowrap; }
.risk-high   { background: #fee2e2; color: #b91c1c; }
.risk-medium { background: #fef3c7; color: #92400e; }
.risk-low    { background: #d1fae5; color: #065f46; }

/* ── Score chip ───────────────────────────────────────────────────── */
.score-chip { display: inline-block; padding: 2px 8px; border-radius: 6px; font-size: .78rem; font-weight: 700; }
.chip-high { background: #d1fae5; color: #065f46; }
.chip-mid  { background: #fef3c7; color: #92400e; }
.chip-low  { background: #fee2e2; color: #991b1b; }

/* ── DMI chip ─────────────────────────────────────────────────────── */
.dmi-chip { display: inline-block; padding: 2px 8px; border-radius: 6px; font-size: .78rem; font-weight: 700; }
.dmi-high { background: #dbeafe; color: #1e40af; }
.dmi-mid  { background: #e0e7ff; color: #3730a3; }
.dmi-low  { background: #f3e8ff; color: #6b21a8; }
.dmi-na   { background: #f1f5f9; color: #94a3b8; }
.metric-main { font-weight: 700; }

/* ── Spinner ──────────────────────────────────────────────────────── */
.spinner { display: inline-block; width: 18px; height: 18px; flex-shrink: 0; border: 2px solid #e5e7eb; border-top-color: #1d4ed8; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Legend ───────────────────────────────────────────────────────── */
.legend-card { background: #fff; border-radius: 10px; padding: 16px 20px; box-shadow: 0 1px 4px rgba(0,0,0,.07); }
.legend-title { font-size: .78rem; font-weight: 700; color: #374151; margin-bottom: 10px; }
.legend-items { display: flex; flex-wrap: wrap; gap: 12px 24px; }
.legend-item { display: flex; align-items: center; gap: 8px; font-size: .75rem; color: #6b7280; }
.legend-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }

@media (max-width: 640px) {
  .summary-row { grid-template-columns: repeat(2, 1fr); }
  .page-header { flex-direction: column; }
  .header-controls { flex-direction: column; align-items: flex-start; }
}

/* ── Prob info button ─────────────────────────────────────────── */
.prob-info-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px; border-radius: 50%;
  background: #dbeafe; color: #1d4ed8; border: none;
  font-size: .6rem; font-weight: 700; cursor: pointer;
  margin-left: 5px; vertical-align: middle; line-height: 1;
  transition: background .15s, color .15s; flex-shrink: 0;
}
.prob-info-btn:hover { background: #1d4ed8; color: #fff; }

/* ── Forfeit Probability Modal ───────────────────────────────── */
.prob-modal-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(15,23,42,.55); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center; padding: 24px;
}
.prob-modal {
  background: #fff; border-radius: 14px; width: 100%; max-width: 520px;
  box-shadow: 0 8px 40px rgba(0,0,0,.22); overflow: hidden;
}
.prob-modal-hdr {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px 14px;
  background: linear-gradient(135deg,#1e3a8a 0%,#1d4ed8 100%);
}
.prob-modal-title { font-size: .95rem; font-weight: 700; color: #fff; }
.prob-modal-close {
  background: rgba(255,255,255,.18); border: none; color: #fff;
  border-radius: 6px; width: 28px; height: 28px; cursor: pointer;
  font-size: .85rem; display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.prob-modal-close:hover { background: rgba(255,255,255,.35); }
.prob-modal-body { padding: 18px 20px 20px; }
.prob-modal-lead { font-size: .84rem; color: #374151; line-height: 1.6; margin: 0 0 14px; }
.prob-modal-lead strong { color: #111827; }
.prob-modal-lead em { font-style: normal; font-weight: 700; color: #1d4ed8; }
.prob-modal-formula {
  background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 8px;
  padding: 10px 14px; margin-bottom: 14px;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.prob-formula-label { font-size: .7rem; font-weight: 700; color: #0369a1; text-transform: uppercase; letter-spacing: .4px; white-space: nowrap; }
.prob-modal-formula code { font-family: monospace; font-size: .83rem; font-weight: 700; color: #0c4a6e; background: none; padding: 0; }
.prob-modal-factors { border: 1px solid #e5e7eb; border-radius: 9px; overflow: hidden; margin-bottom: 14px; }
.pmf-row { display: flex; align-items: flex-start; gap: 12px; padding: 11px 14px; border-bottom: 1px solid #f3f4f6; }
.pmf-badge { width: 28px; height: 28px; border-radius: 7px; font-size: .7rem; font-weight: 800; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.pmf-p1 { background: #fee2e2; color: #b91c1c; }
.pmf-p2 { background: #fef3c7; color: #92400e; }
.pmf-p3 { background: #fff7ed; color: #c2410c; }
.pmf-p4 { background: #eff6ff; color: #1d4ed8; }
.pmf-content { flex: 1; min-width: 0; }
.pmf-name { font-size: .82rem; font-weight: 700; color: #111827; margin-bottom: 3px; }
.pmf-weight { font-size: .72rem; font-weight: 600; color: #6b7280; margin-left: 6px; }
.pmf-desc { font-size: .76rem; color: #6b7280; line-height: 1.5; }
.prob-modal-levels { background: #f8faff; border: 1px solid #e2e6ea; border-radius: 8px; padding: 11px 14px; margin-bottom: 12px; }
.pml-title { font-size: .65rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 8px; }
.pml-row { display: flex; align-items: center; gap: 8px; margin-bottom: 5px; }
.pml-row:last-child { margin-bottom: 0; }
.pml-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.pml-high { background: #dc2626; }
.pml-mid  { background: #d97706; }
.pml-low  { background: #059669; }
.pml-label { font-size: .8rem; font-weight: 600; color: #374151; flex: 1; }
.pml-range { font-size: .78rem; font-weight: 700; color: #6b7280; font-family: monospace; }
.prob-modal-note { font-size: .72rem; color: #9ca3af; line-height: 1.5; margin: 0; padding-top: 2px; }

/* ── Print styles ─────────────────────────────────────────────── */
@media print {
  @page { size: landscape; margin: 10mm 12mm; }
  .page-wrap > *:not(.section-card) { display: none !important; }
  .page-wrap { padding: 0; }
  .section-card { box-shadow: none; border-radius: 0; border: none; }
  .section-head, .export-btn, .prob-info-btn, .principal-strip { display: none !important; }
  .print-header {
    display: block !important;
    text-align: center; padding: 6px 0 8px;
  }
  .ph-title     { font-size: 13pt; font-weight: 700; color: #000; margin-bottom: 4px; }
  .ph-meta      { font-size: 8pt;  color: #333; margin-bottom: 2px; }
  .ph-principal { font-size: 8pt;  font-weight: 600; color: #000; }
  .table-wrap { max-height: none !important; overflow: visible !important; }
  .col-extra, .col-ticket, .col-due { display: table-cell !important; }
  table { border-collapse: collapse; width: 100%; font-size: 8pt; }
  thead th {
    position: static !important;
    background: #fff !important; color: #000 !important;
    border: 1px solid #000 !important;
    padding: 4px 5px; font-size: 8pt; font-weight: 700;
  }
  tbody td {
    background: #fff !important; color: #000 !important;
    border: 1px solid #000 !important;
    padding: 3px 5px; font-size: 8pt;
  }
  tbody tr, .row-high, .row-medium, .top3 { background: #fff !important; }
  .risk-badge, .score-chip, .dmi-chip, .ticket-no,
  .asset-type-tag, .phlat-tag, .overdue-badge {
    background: none !important; border: none !important;
    padding: 0 !important; border-radius: 0 !important;
    color: #000 !important; font-size: 8pt !important; font-weight: normal !important;
  }
  .rank {
    background: none !important; color: #000 !important;
    border-radius: 0 !important; width: auto !important; height: auto !important;
    font-size: 8pt !important; font-weight: normal !important; display: inline !important;
  }
  .prob-bar-bg { display: none !important; }
  .prob-pct    { color: #000 !important; font-size: 8pt !important; }
  .prob-wrap   { justify-content: flex-end; }
  .tag-red, .tag-amber, .tag-green { color: #000 !important; font-weight: normal !important; }
  tbody tr { page-break-inside: avoid; }
}
</style>
